<template>
    <div v-if="isVisible" class="s-purchasing-acp-banner">
        <div class="wrapper">
            <div class="content">
                <div class="inner">
                    <p class="title" v-html="content.title" />
                    <p class="description" v-html="content.description" />
                    <div class="offer">
                        <p v-html="content.offer" />
                        <p v-if="content.cleverbridge?.coupon" class="coupon">
                            {{ content.cleverbridge.coupon }}
                        </p>
                    </div>
                    <p class="note" v-html="content.note" />
                    <a-button
                        v-if="content.button"
                        v-bind="content.button"
                        :event="{ doNotSendGA: true }"
                        @click="handleClick()"
                    />
                </div>
                <div v-if="hasPicture" class="inner-picture">
                    <a-picture v-bind="content.picture" />
                </div>
            </div>
            <a-button
                class="close"
                glyph="close"
                :event="{ doNotSendGA: true }"
                @click="handleClose()"
            />
        </div>
    </div>
</template>

<script>
import AButton from '@core/components/button/button.vue';
import APicture from '@core/components/picture/picture.vue';
import analytics from '@core/mixins/analytics.js';
import breakpoint from '@core/mixins/breakpoint.js';
import cleverbridge from '@core/mixins/cleverbridge.js';
import currency from '@core/mixins/currency.js';
import mixin from '../mixin.js';

export default {
    name: 'SBanner',

    components: {
        AButton,
        APicture,
    },

    mixins: [breakpoint, mixin, analytics, currency, cleverbridge],

    props: {
        settings: {
            type: Object,
            default: null,
        },
    },

    emits: ['onClose', 'showLoaderSURL'],

    data: () => ({
        isVisible: false,
    }),

    computed: {
        content() {
            return this.settings.banner || {};
        },
        hasPicture() {
            if (!this.content.picture) return false;
            return ['desktopWide', 'desktopLarge'].includes(this.currentBreakpoint);
        },
    },

    beforeDestroy() {
        document.getElementsByTagName('body')[0].removeEventListener('mouseleave', this.onMouseLeave);
    },

    mounted() {
        document.getElementsByTagName('body')[0].addEventListener('mouseleave', this.onMouseLeave);
    },

    methods: {
        sendEvent(event) {
            if (!event) return;
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'Acronis',
                eventButton: '',
                eventCategory: 'Popup',
                eventContext: '',
                eventLabel: window.location.pathname,
                eventLocation: 'exit popup',
                ...event,
            });
        },
        handleClick() {
            this.sendEvent(this.content.eventsGA?.click);
            const banner = this.settings.banner || {};
            const coupon = banner.cleverbridge?.coupon || '';
            const eventEcommerce = banner.eventsGA?.ecommerce || {};
            this.$emit('showLoaderSURL');
            this.onClickBuyButton(eventEcommerce, coupon);
        },
        handleClose() {
            this.sendEvent(this.content.eventsGA?.close);
            this.$emit('onClose');
        },
        onMouseLeave(event) {
            if (event.pageY > window.pageYOffset) return;

            if (!this.content) return;
            if (this.isVisible) return;
            if (!this.addedLicenses.length) return;

            const productPromoList = this.content.cleverbridge?.products || [];
            if (!productPromoList.length) return;

            const productAddedlist = this.addedLicenses.map((item) => item.cleverBridgeCartID);
            if (!productAddedlist.some((item) => productPromoList.includes(item))) return;

            const promo = this.settings?.promo || {};
            if (promo.isPromoActive && productAddedlist.some((item) => promo.cleverBridgeCartList.includes(item))) return;

            this.sendEvent(this.content.eventsGA?.open);
            this.isVisible = true;
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-purchasing-acp-banner {
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    z-index: 1000;
    padding: 48px;
    overflow: auto;
    position: fixed;
    align-items: center;
    flex-direction: column;
    inset-inline-start: 0;
    background: var(--av-fixed-secondary);
    .wrapper {
        margin: auto 0;
    }
    .content {
        display: flex;
        overflow: hidden;
        border-radius: 8px;

        transition: 4s;
        animation: opacity 4s 1;
        animation-fill-mode: forwards;

        box-shadow: 0 10px 20px 0 rgba(36, 49, 67, 0.90);
    }
    .inner {
        width: 100%;
        display: flex;
        max-width: 482px;
        background: #fff;
        text-align: start;
        min-height: 552px;
        position: relative;
        flex-direction: column;
        padding: 24px 16px 16px;
        color: var(--av-nav-primary);
        @media (--viewport-tablet) {
            padding: 48px 40px;
        }
    }
    .title {
        @mixin hero-accent;
    }
    .description {
        @mixin title;
        margin: 24px 0 40px;
        @media (--viewport-desktop-wide) {
            @mixin lead;
            margin-top: 16px;
        }
    }
    .offer {
        @mixin title;
        width: 100%;
        margin: auto;
        padding: 12px;
        border-radius: 8px;
        text-align: center;
        background: var(--av-brand-secondary-accent);
    }
    .coupon {
        @mixin title-accent;
        margin-top: 8px;
    }
    .note {
        @mixin paragraph;
        text-align: center;
        margin-top: 40px;
        color: var(--av-fixed-light);
    }
    .a-button {
        margin-top: 8px;
    }
    .inner-picture {
        width: 480px;
        background: #fff;
        position: relative;
        &:deep(.a-picture) {
            top: 0;
            width: 100%;
            height: 100%;
            position: absolute;
            inset-inline-start: 0;
            .a-picture__img {
                height: 100%;
            }
        }
    }

    &:deep(.close) {
        top: 0;
        margin: 0;
        display: flex;
        padding: 8px;
        position: absolute;
        border-radius: 50%;
        inset-inline-end: -48px;
        border: 1px solid var(--av-inversed-primary);
        .a-glyph {
            fill: var(--av-inversed-primary);
        }
    }
}

@keyframes opacity{
    0%{
        opacity:0;
    }
    100% {
        opacity:1;
    }
}
</style>
